import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ShoppingBag,
  Bell,
  Menu,
  X,
  LogOut,
  User as UserIcon,
  LayoutDashboard,
  Sparkles,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useCartTransition } from "@/context/CartTransition";
import { useNotifications } from "@/context/NotificationsContext";
import { getTheme } from "@/lib/theme";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/deals", label: "Deals" },
  { to: "/large-order", label: "Large Order" },
  { to: "/about", label: "About" },
];

export function Navbar() {
  const { user, selectedBusinessId, setSelectedBusinessId, signOut } = useAuth();
  const { countFor } = useCart();
  const { triggerCartTransition } = useCartTransition();
  const { unread } = useNotifications();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const theme = getTheme(selectedBusinessId);
  const other = selectedBusinessId === "baking_jays" ? "rubys" : "baking_jays";
  const count = countFor(selectedBusinessId);

  const switchBusiness = () => {
    setSelectedBusinessId(other);
    nav("/shop");
    setOpen(false);
  };

  const isActive = (to) => loc.pathname === to || loc.pathname.startsWith(to + "/");

  return (
    <header
      className="sticky top-0 z-40 border-b backdrop-blur"
      style={{ background: "color-mix(in srgb, var(--bg) 88%, transparent)", borderColor: "var(--border)" }}
    >
      <div className="container-app flex items-center justify-between h-16">
        <div className="flex items-center gap-3">
          <Link to="/shop" className="flex items-center gap-2.5" data-testid="navbar-logo">
            <img
              src={theme.logo}
              alt={theme.name}
              className="h-10 w-10 rounded-full object-cover crest-spin"
              style={{ boxShadow: "0 0 0 1px var(--border), 0 4px 14px rgba(0,0,0,0.35)" }}
            />
            <div className="leading-tight hidden sm:block">
              <div className="font-display text-lg" style={{ color: "var(--ink)" }}>
                {theme.name}
              </div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--muted)" }}>
                {theme.kind}
              </div>
            </div>
          </Link>
        </div>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="px-3.5 py-2 rounded-full text-sm font-semibold transition"
              style={
                isActive(n.to)
                  ? { background: "var(--surface-2)", color: "var(--ink)" }
                  : { color: "var(--muted)" }
              }
            >
              {n.label}
            </Link>
          ))}
          {user?.role === "baker" && (
            <Link
              to="/manage"
              data-testid="nav-manage"
              className="px-3.5 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5"
              style={{ color: "var(--brand)" }}
            >
              <LayoutDashboard size={16} /> Dashboard
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-1.5">
          <button
            onClick={switchBusiness}
            data-testid="switch-business-btn"
            className="hidden lg:flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition hover:opacity-80"
            style={{ border: "1px solid var(--border)", color: "var(--muted)" }}
            title={`Switch to ${getTheme(other).name}`}
          >
            <Sparkles size={14} /> {getTheme(other).name}
          </button>

          {user && (
            <Link
              to="/notifications"
              data-testid="nav-notifications"
              className="relative p-2.5 rounded-full transition hover:bg-[var(--surface-2)]"
              aria-label="Notifications"
            >
              <Bell size={20} style={{ color: "var(--ink)" }} />
              {unread > 0 && (
                <span
                  className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                  style={{ background: "var(--accent)", color: "#1a1400" }}
                >
                  {unread}
                </span>
              )}
            </Link>
          )}

          <Link
            to="/cart"
            data-testid="nav-cart"
            onClick={(e) => { if (loc.pathname !== "/cart") { e.preventDefault(); triggerCartTransition(); } }}
            className="relative p-2.5 rounded-full transition hover:bg-[var(--surface-2)]"
            aria-label="Cart"
          >
            <ShoppingBag size={20} style={{ color: "var(--ink)" }} />
            {count > 0 && (
              <span
                className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                style={{ background: "var(--brand)", color: "var(--brand-fg)" }}
              >
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-btn"
                  className="hidden md:flex items-center gap-1.5 pl-2.5 pr-2 py-1.5 rounded-full transition hover:bg-[var(--surface-2)]"
                >
                  <div
                    className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: "var(--accent-soft)", color: "var(--brand)" }}
                  >
                    {user.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <ChevronDown size={14} style={{ color: "var(--muted)" }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-sm">
                  <div className="font-semibold truncate">{user.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                {user.role === "customer" && (
                  <DropdownMenuItem onClick={() => nav("/my-orders")} data-testid="menu-my-orders">
                    <ShoppingBag size={15} className="mr-2" /> My Orders
                  </DropdownMenuItem>
                )}
                {user.role === "baker" && (
                  <DropdownMenuItem onClick={() => nav("/manage")} data-testid="menu-dashboard">
                    <LayoutDashboard size={15} className="mr-2" /> Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => nav("/account")} data-testid="menu-account">
                  <UserIcon size={15} className="mr-2" /> Account
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    signOut();
                    nav("/shop");
                  }}
                  data-testid="menu-signout"
                >
                  <LogOut size={15} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link
              to="/sign-in"
              data-testid="nav-signin"
              className="hidden md:inline-flex btn-brand px-4 py-2 rounded-full text-sm font-semibold"
            >
              Sign in
            </Link>
          )}

          <button
            className="md:hidden p-2.5 rounded-full"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t px-5 py-3 space-y-1" style={{ borderColor: "var(--border)" }}>
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              className="block px-3 py-2.5 rounded-lg font-semibold"
              style={{ color: "var(--ink)" }}
              data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </Link>
          ))}
          {user?.role === "baker" && (
            <Link to="/manage" onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg font-semibold" style={{ color: "var(--brand)" }}>
              Dashboard
            </Link>
          )}
          {user?.role === "customer" && (
            <Link to="/my-orders" onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg font-semibold" style={{ color: "var(--ink)" }}>
              My Orders
            </Link>
          )}
          <button onClick={switchBusiness} className="block w-full text-left px-3 py-2.5 rounded-lg font-semibold" style={{ color: "var(--muted)" }}>
            Switch to {getTheme(other).name}
          </button>
          {user ? (
            <button
              onClick={() => {
                signOut();
                setOpen(false);
                nav("/shop");
              }}
              className="block w-full text-left px-3 py-2.5 rounded-lg font-semibold text-red-600"
            >
              Sign out
            </button>
          ) : (
            <Link to="/sign-in" onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg font-semibold" style={{ color: "var(--brand)" }}>
              Sign in
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
